import { useCreateDesign } from 'components/Wizards/DesignWizard/useCreateDesign'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Values, TextChannelToDesignChannel } from 'components/Wizards/DesktopWizards/Types'
import { AlmanacFile, VisualGenerateRequest } from 'types'
import { WizardType } from 'components/Wizards/schemas/Types'
import { CrossPostAccount } from 'components/Wizards/Wizard/Form/CrossPostAccountsInput/CrossPostAccountsInput'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'

type StartFn = () => () => void
type SuccessFn = (params: {
  design: VisualGenerateRequest['almanac_file'] | undefined
  otherAccounts: CrossPostAccount[] | undefined
}) => void
type ErrorFn = (error: unknown) => void
type SettledFn = () => void

export interface UseCreateDesignMutationOptions {
  workspaceId: number
  channel: WizardType
  currentFolder: AlmanacFile | null
  defaultFolder?: AlmanacFile | null

  onGenerationStart?: StartFn
  onSuccess?: SuccessFn
  onError?: ErrorFn
  onSettled?: SettledFn
}

export const useCreateDesignMutation = ({
  workspaceId,
  channel,
  currentFolder,
  defaultFolder,

  onGenerationStart,
  onSuccess,
  onError,
  onSettled,
}: UseCreateDesignMutationOptions) => {
  const { mutateAsync } = useCreateDesign({ workspaceId })
  const isScheduledPostsV3 = useIsScheduledPostsV3()

  const createDesign = async (values: Values) => {
    const stopSpinner = onGenerationStart?.()

    try {
      // Convert channel to Design channel if isScheduledPostsV3 is true
      let convertedChannel = channel
      if (isScheduledPostsV3 && channel in TextChannelToDesignChannel) {
        convertedChannel = TextChannelToDesignChannel[channel as ChannelType] as WizardType
      }
      const {
        width,
        height,
        filename,
        project,
        source_visual_element_id,
        brand_visual_kit_id,
        cross_post_accounts,
        topic,
        brand_voice_id,
        tone,
        language,
        caption,
        brand_kit_id,
        content,
        initial_media_file_id,
        initial_media_file_remove_background,
        archetype,
        links,
      } = values

      const seed = { apply_brand: false }

      const firstAccount = cross_post_accounts?.find(
        (acc) => acc.provider === providerFromChannelType[convertedChannel as DesignChannel],
      )
      const otherAccounts = cross_post_accounts?.filter(
        (acc) => acc.external_integration_id !== firstAccount?.external_integration_id,
      )

      const parent_id = project?.id || currentFolder?.id || defaultFolder?.id || ''
      const external_integration_id = firstAccount?.external_integration_id

      const design = await mutateAsync({
        width,
        height,
        topic,
        brand_voice_id,
        tone,
        language,
        source_visual_element_id,
        title: filename,
        parent_id,
        channel: convertedChannel,
        initial_media_file_id,
        initial_media_file_remove_background,
        external_integration_id,
        brand_visual_kit_id,
        captions: caption ? [caption] : undefined,
        selected_caption_id: caption ? 0 : undefined,
        cross_post_accounts: otherAccounts || [],
        seed,
        brand_kit_id,
        content,
        archetype,
        links,
        completed_actions: {
          template: Boolean(source_visual_element_id),
          generate_caption: Boolean(topic),
          core_image: Boolean(initial_media_file_id),
          apply_brand: seed.apply_brand,
        },
      })

      if (!design) throw new Error('No almanac file found')

      onSuccess?.({ design: { ...design, external_integration_id }, otherAccounts })
      return design
    } catch (err) {
      onError?.(err)
      throw err
    } finally {
      stopSpinner?.()
      onSettled?.()
    }
  }

  return { createDesign }
}
